(() => {
  const root = document.querySelector('[data-practitioner-blog]');
  if (!root) return;

  const grid = root.querySelector('.practitioner-blog-grid');
  const title = root.querySelector('[data-results-title]');
  const search = root.querySelector('[data-blog-search]');
  const sort = root.querySelector('[data-blog-sort]');
  const empty = root.querySelector('[data-blog-empty]');
  const typeList = root.querySelector('[data-type-list]');
  const typeChips = root.querySelector('[data-type-chips]');
  const topicChips = root.querySelector('[data-topic-chips]');
  const defaultTitle = root.dataset.heading || 'All Articles';
  const types = ['Articles', 'Research', 'Podcasts', 'Blogs', 'Webinars', 'In-Person Events'];
  const topicOrder = ['Detox', 'Cellular Health', 'Gut Health', 'Protocols', 'Sleep & Stress', 'Drainage', 'Toxins'];

  const state = { type: 'all', topic: '', query: '', sort: 'recent' };
  let cards = [...root.querySelectorAll('[data-blog-card]')];
  let searchTimer = 0;
  let writingUrl = false;

  const normalize = (value) => (value || '').trim().toLowerCase();
  const slug = (value) => normalize(value).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const labelFor = (value) => {
    const known = types.find((type) => slug(type) === slug(value));
    if (known) return known;
    const fromCard = cards.map((card) => card.dataset.type).find((type) => slug(type) === slug(value));
    return fromCard || value;
  };

  const topicValues = (card) => (card.dataset.topics || '').split('|').map((topic) => topic.trim()).filter(Boolean);

  const foldWords = (value) => normalize(value).replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean);
  const stem = (word) => (word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word);

  const editsWithinOne = (left, right) => {
    if (left === right) return 0;
    if (Math.abs(left.length - right.length) > 1) return 2;
    let i = 0;
    let j = 0;
    let edits = 0;
    while (i < left.length && j < right.length) {
      if (left[i] === right[j]) {
        i += 1;
        j += 1;
      } else {
        edits += 1;
        if (edits > 1) return 2;
        if (left.length > right.length) i += 1;
        else if (right.length > left.length) j += 1;
        else {
          i += 1;
          j += 1;
        }
      }
    }
    if (i < left.length || j < right.length) edits += 1;
    return edits > 1 ? 2 : edits;
  };

  const tokenScore = (token, field) => {
    const stemmed = stem(token);
    let best = 0;
    field.words.forEach((word) => {
      const folded = stem(word);
      const edits = editsWithinOne(stemmed, folded);
      if (edits === 0 || (edits === 1 && token.length >= 5)) {
        const points = (edits === 0 ? 100 : 60) * field.weight;
        if (points > best) best = points;
        return;
      }
      if (token.length >= 2 && token.length < word.length && (word.startsWith(token) || folded.startsWith(stemmed))) {
        const points = 80 * field.weight;
        if (points > best) best = points;
      }
    });
    if (!best && token.length >= 4 && field.compact.includes(token)) {
      best = 40 * field.weight;
    }
    return best;
  };

  const searchScore = (card) => {
    const tokens = foldWords(state.query);
    if (!tokens.length) return 0;
    const fields = [
      { words: foldWords(card.dataset.title), weight: 3 },
      { words: foldWords(card.dataset.excerpt), weight: 1 },
      { words: foldWords(card.dataset.type), weight: 1 },
      { words: foldWords((card.dataset.topics || '').replaceAll('|', ' ')), weight: 1 },
    ].map((field) => ({ ...field, compact: field.words.join('') }));
    const queryCompact = tokens.join('');
    const required = tokens.filter((token) => token.length >= 2);
    const optional = tokens.filter((token) => token.length < 2);
    let total = 0;
    if (fields[0].compact === queryCompact) total += 1000;
    else if (queryCompact && fields[0].compact.includes(queryCompact)) total += 500;
    const scoreToken = (token) => fields.reduce((highest, field) => Math.max(highest, tokenScore(token, field)), 0);
    const matched = (required.length ? required : optional).every((token) => {
      const best = scoreToken(token);
      total += best;
      return best > 0;
    });
    if (!matched) return null;
    if (required.length) {
      optional.forEach((token) => {
        const bonus = fields.reduce((highest, field) => {
          const prefix = field.words.some((word) => word.startsWith(token)) ? 80 * field.weight : 0;
          return Math.max(highest, tokenScore(token, field), prefix);
        }, 0);
        total += bonus;
      });
    }
    return total;
  };

  const passesFilters = (card) => {
    const typeMatch = state.type === 'all' || normalize(card.dataset.type) === normalize(state.type);
    const topicMatch = !state.topic || topicValues(card).some((topic) => slug(topic) === slug(state.topic));
    return typeMatch && topicMatch;
  };

  const bySelectedSort = (a, b) => {
    if (state.sort === 'alpha') return a.dataset.title.localeCompare(b.dataset.title);
    if (state.sort === 'popular') {
      const popularity = Number(b.dataset.popularity || 0) - Number(a.dataset.popularity || 0);
      if (popularity) return popularity;
    }
    return Number(b.dataset.date || 0) - Number(a.dataset.date || 0);
  };

  const readUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    state.type = type ? labelFor(type) : 'all';
    state.topic = params.get('topic') || '';
    state.query = params.get('q') || '';
    const sortValue = params.get('sort');
    state.sort = ['alpha', 'popular', 'recent'].includes(sortValue) ? sortValue : 'recent';
    if (search) search.value = state.query;
    if (sort) sort.value = sortValue ? state.sort : '';
  };

  const writeUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.delete('page');
    const set = (key, value) => {
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    };
    set('type', state.type === 'all' ? '' : slug(state.type));
    set('topic', state.topic ? slug(state.topic) : '');
    set('sort', state.sort === 'recent' ? '' : state.sort);
    set('q', state.query);
    writingUrl = true;
    history.pushState(null, '', url);
    writingUrl = false;
  };

  const topicsForType = () => {
    const seen = new Map();
    cards.forEach((card) => {
      if (state.type !== 'all' && normalize(card.dataset.type) !== normalize(state.type)) return;
      topicValues(card).forEach((topic) => {
        if (!seen.has(slug(topic))) seen.set(slug(topic), topic);
      });
    });
    const rank = (topic) => {
      const index = topicOrder.findIndex((name) => slug(name) === slug(topic));
      return index === -1 ? topicOrder.length : index;
    };
    return [...seen.values()].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  };

  const topicButton = (topic, className) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.dataset.filter = 'topic';
    button.dataset.value = topic;
    button.textContent = topic;
    return button;
  };

  const renderTopics = () => {
    const topics = topicsForType();
    if (state.topic && !topics.some((topic) => slug(topic) === slug(state.topic))) state.topic = '';

    typeList?.querySelectorAll('.practitioner-blog-filter__topics').forEach((list) => list.remove());
    const selectedType = [...(typeList?.querySelectorAll('[data-filter="type"]') || [])]
      .find((button) => normalize(button.dataset.value) === normalize(state.type));
    typeList?.querySelectorAll('[data-filter="type"]').forEach((button) => {
      button.setAttribute('aria-expanded', button === selectedType && topics.length > 0 ? 'true' : 'false');
    });
    if (selectedType && topics.length) {
      const list = document.createElement('ul');
      list.className = 'practitioner-blog-filter__topics';
      topics.forEach((topic) => {
        const item = document.createElement('li');
        item.append(topicButton(topic, 'practitioner-blog-filter__topic'));
        list.append(item);
      });
      selectedType.closest('li')?.append(list);
    }

    if (!topicChips) return;
    topicChips.replaceChildren();
    topicChips.hidden = topics.length === 0;
    if (!topics.length) return;

    const topicChip = (label, value) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'practitioner-blog-topic-chip';
      button.dataset.filter = 'topic';
      button.dataset.value = value;
      const icon = document.createElement('span');
      icon.className = 'practitioner-blog-topic-chip__icon';
      icon.setAttribute('aria-hidden', 'true');
      const name = document.createElement('span');
      name.textContent = label;
      const clear = document.createElement('span');
      clear.className = 'practitioner-blog-topic-chip__x';
      clear.setAttribute('aria-hidden', 'true');
      clear.textContent = '×';
      button.append(icon, name, clear);
      return button;
    };

    topicChips.append(topicChip('All Topics', ''));
    topics.forEach((topic) => topicChips.append(topicChip(topic, topic)));
  };

  const presentTypes = () => {
    const seen = new Map();
    cards.forEach((card) => {
      const type = (card.dataset.type || '').trim();
      if (!type || seen.has(normalize(type))) return;
      seen.set(normalize(type), type);
    });
    const rank = (label) => {
      const index = types.findIndex((type) => normalize(type) === normalize(label));
      return index === -1 ? types.length : index;
    };
    return [...seen.values()].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  };

  const renderTypes = () => {
    const labels = presentTypes();
    if (state.type !== 'all' && !labels.some((label) => normalize(label) === normalize(state.type))) {
      state.type = 'all';
      state.topic = '';
    }
    typeList?.querySelectorAll('[data-generated-type]').forEach((item) => item.remove());
    typeChips?.querySelectorAll('[data-generated-type]').forEach((item) => item.remove());
    labels.forEach((label) => {
      const count = cards.filter((card) => normalize(card.dataset.type) === normalize(label)).length;
      const item = document.createElement('li');
      item.className = 'practitioner-blog-filter__item';
      item.dataset.generatedType = '';
      const button = document.createElement('button');
      button.className = 'practitioner-blog-filter__button';
      button.type = 'button';
      button.dataset.filter = 'type';
      button.dataset.value = label;
      const name = document.createElement('span');
      name.textContent = label;
      const total = document.createElement('span');
      total.className = 'practitioner-blog-filter__count';
      total.dataset.countFor = label;
      total.textContent = String(count);
      button.append(name, total);
      item.append(button);
      typeList?.append(item);

      const chip = document.createElement('button');
      chip.className = 'practitioner-blog-chip';
      chip.type = 'button';
      chip.dataset.filter = 'type';
      chip.dataset.value = label;
      chip.dataset.generatedType = '';
      chip.textContent = label;
      typeChips?.append(chip);
    });
  };

  const render = () => {
    renderTypes();
    renderTopics();
    const ranked = cards.flatMap((card) => {
      if (!passesFilters(card)) return [];
      const score = state.query ? searchScore(card) : 0;
      if (score == null) return [];
      return [{ card, score }];
    }).sort((a, b) => {
      if (state.query && a.score !== b.score) return b.score - a.score;
      return bySelectedSort(a.card, b.card);
    });

    cards.forEach((card) => {
      card.hidden = true;
    });
    ranked.forEach(({ card }) => {
      card.hidden = false;
      empty.before(card);
    });
    if (empty) empty.hidden = ranked.length > 0;

    root.querySelectorAll('[data-count-for]').forEach((count) => {
      const key = count.dataset.countFor;
      const total = key === 'all'
        ? cards.length
        : cards.filter((card) => normalize(card.dataset.type) === normalize(key)).length;
      count.textContent = String(total);
    });

    root.querySelectorAll('[data-filter]').forEach((button) => {
      const active = button.dataset.filter === 'type'
        ? normalize(button.dataset.value) === normalize(state.type)
        : button.dataset.value
          ? Boolean(state.topic) && slug(button.dataset.value) === slug(state.topic)
          : !state.topic;
      if (active) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });

    if (title) title.textContent = state.topic || (state.type === 'all' ? defaultTitle : state.type);
    const sortLabel = root.querySelector('[data-sort-label]');
    const sortBox = sort?.closest('.practitioner-blog-sort');
    const sortIcon = sortBox?.querySelector('img');
    if (sort && sortLabel && sortBox && sortIcon) {
      const selected = sort.options[sort.selectedIndex]?.textContent || 'Sort by';
      sortLabel.style.marginRight = '0px';
      sortLabel.textContent = 'Sort by';
      const sortByWidth = sortLabel.getBoundingClientRect().width;
      const boxStyle = getComputedStyle(sortBox);
      const iconSpace = parseFloat(getComputedStyle(sortIcon).right) + sortIcon.offsetWidth;
      const restingGap = parseFloat(boxStyle.minWidth) - parseFloat(boxStyle.paddingLeft) - sortByWidth - iconSpace;
      const builtInGap = parseFloat(boxStyle.paddingRight) - iconSpace;
      sortLabel.textContent = selected;
      sortLabel.style.marginRight = `${Math.max(0, restingGap - builtInGap)}px`;
    }
  };

  const loadRemaining = async () => {
    const pages = Number(root.dataset.pages || 1);
    const sectionId = root.dataset.sectionId;
    for (let page = 2; page <= pages; page += 1) {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('page', String(page));
      const response = await fetch(url);
      if (!response.ok) break;
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      parsed.querySelectorAll('[data-blog-card]').forEach((card) => {
        empty.before(card);
        cards.push(card);
      });
    }
  };

  root.addEventListener('click', (event) => {
    const button = event.target.closest('[data-filter]');
    if (!button || !root.contains(button)) return;
    if (button.dataset.filter === 'type') {
      state.type = button.dataset.value;
      state.topic = '';
    } else {
      state.topic = slug(button.dataset.value) === slug(state.topic) ? '' : button.dataset.value;
    }
    render();
    writeUrl();
  });

  if (search) {
    search.addEventListener('input', () => {
      state.query = search.value.trim();
      render();
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(writeUrl, 300);
    });
  }

  if (sort) {
    sort.addEventListener('change', () => {
      state.sort = sort.value;
      render();
      writeUrl();
    });
  }

  window.addEventListener('popstate', () => {
    if (writingUrl) return;
    readUrl();
    render();
  });

  readUrl();
  loadRemaining()
    .catch(() => {})
    .finally(() => {
      render();
      root.dataset.blogState = 'ready';
    });
})();
